import { useState, useRef, useEffect } from "react";
import axios from "axios";
import useTranslation from "@/hooks/useTranslation";
import { toast } from "sonner";
import {
  Copy, Check, Sparkles, Loader2, Star, Flame, RefreshCcw, Bot, Film, Share2, Phone, Import, Clock, Bookmark, Layers, ShieldCheck, BarChart3, MessageSquare, Target, Calendar, ShieldAlert, Home, Mic, Link2, Box, Lock, Gift, Fingerprint, Globe, MapPin, LayoutGrid,
} from "lucide-react";
import VideoBuilder from "@/components/VideoBuilder";
import AdvisorPanel from "@/components/AdvisorPanel";
import PaywallModal from "@/components/PaywallModal";
import ExpiredListingScripts from "@/components/ExpiredListingScripts";
import RedfinImport from "@/components/RedfinImport";
import ListingHistory from "@/components/ListingHistory";
import SavedTemplates from "@/components/SavedTemplates";
import BatchGenerator from "@/components/BatchGenerator";
import EmailCapture from "@/components/EmailCapture";
import ContractReview from "@/components/ContractReview";
import SellerDashboard from "@/components/SellerDashboard";
import LeadNurture from "@/components/LeadNurture";
import LeadScore from "@/components/LeadScore";
import TransactionTracker from "@/components/TransactionTracker";
import OpenHousePanel from "@/components/OpenHousePanel";
import FairHousingPanel from "@/components/FairHousingPanel";
import COActPanel from "@/components/COActPanel";
import VoiceDescriptionPanel from "@/components/VoiceDescriptionPanel";
import PostSaleReportPanel from "@/components/PostSaleReportPanel";
import AgentBioPanel from "@/components/AgentBioPanel";
import PhotoToListing from "@/components/PhotoToListing";
import ReferralPanel from "@/components/ReferralPanel";
import BrandVoicePanel from "@/components/BrandVoicePanel";
import TemplatesPanel from "@/components/TemplatesPanel";
import ContentCalendarPanel from "@/components/ContentCalendarPanel";
import MarketUpdatePanel from "@/components/MarketUpdatePanel";
import SchedulerPanel from "@/components/SchedulerPanel";
import AgentPageSetup from "@/components/AgentPageSetup";
import CMAPanel from "@/components/CMAPanel";
import VirtualStagingPanel from "@/components/VirtualStagingPanel";
import ProToolPreview from "@/components/ProToolPreview";
import { startCheckout } from "@/lib/checkout";
import ShareCard from "@/components/ShareCard";
import ViralPostCard from "@/components/ViralPostCard";
import MissingDetailsPanel from "@/components/MissingDetailsPanel";
import AskListingChat from "@/components/AskListingChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TONES = ["Luxury", "Cozy", "Modern", "Minimalist", "Family", "Investor"];
const MLS_PRESETS = [
  { chars: 0 },
  { chars: 2800 },
  { chars: 3200 },
  { chars: 2800 },
  { chars: 2400 },
  { chars: 2800 },
  { chars: 2800 },
  { chars: 3200 },
  { chars: 2400 },
  { chars: 2000 },
  { chars: 4000 },
];
const TABS = [
  { key: "mls", label: "MLS Description", icon: "🏡" },
  { key: "instagram", label: "Instagram", icon: "📸" },
  { key: "facebook", label: "Facebook", icon: "📘" },
  { key: "headlines", label: "Headlines", icon: "✏️" },
  { key: "email", label: "Email", icon: "✉️" },
  { key: "reel_script", label: "Reel Script", icon: "🎬" },
  { key: "stories", label: "Stories", icon: "⚡" },
  { key: "print_flyer", label: "Print Flyer", icon: "🖨️" },
];

const HERO_TOOLS = [
  { id: "rewrite", labelKey: "rewrite", icon: Sparkles, free: true },
  { id: "staging", labelKey: "staging", icon: Home, free: true },
  { id: "bio", labelKey: "bio", icon: Box, free: true },
  { id: "photo", labelKey: "photo", icon: Import, free: true },
];

const TOOL_CATEGORIES = [
  {
    name: "Content & Copy",
    tools: [
      { id: "rewrite", labelKey: "rewrite", icon: Sparkles, free: true },
      { id: "bio", labelKey: "bio", icon: Box, free: true },
      { id: "agentpage", labelKey: "agentpage", icon: Globe, free: true },
      { id: "brandvoice", labelKey: "brandvoice", icon: Fingerprint, free: true },
      { id: "referral", labelKey: "referral", icon: Gift, free: true },
    ],
  },
  {
    name: "Marketing & Social",
    tools: [
      { id: "templates", labelKey: "templates", icon: LayoutGrid, free: false },
      { id: "calendar", labelKey: "calendar", icon: Calendar, free: false },
      { id: "marketupdate", labelKey: "marketupdate", icon: BarChart3, free: false },
      { id: "scheduler", labelKey: "scheduler", icon: Clock, free: false },
      { id: "nurture", labelKey: "nurture", icon: MessageSquare, free: false },
    ],
  },
  {
    name: "Visual & Media",
    tools: [
      { id: "photo", labelKey: "photo", icon: Import, free: true },
      { id: "staging", labelKey: "staging", icon: Home, free: true },
      { id: "cma", labelKey: "cma", icon: MapPin, free: true },
      { id: "voice", labelKey: "voice", icon: Mic, free: false },
    ],
  },
  {
    name: "Pro Tools",
    tools: [
      { id: "batch", labelKey: "batch", icon: Layers, free: false },
      { id: "expired", labelKey: "expired", icon: Phone, free: false },
      { id: "import", labelKey: "import", icon: Import, free: false },
      { id: "seller", labelKey: "seller", icon: BarChart3, free: false },
      { id: "score", labelKey: "score", icon: Target, free: false },
      { id: "transaction", labelKey: "transaction", icon: Calendar, free: false },
      { id: "openhouse", labelKey: "openhouse", icon: Home, free: false },
      { id: "report", labelKey: "report", icon: BarChart3, free: false },
    ],
  },
  {
    name: "Compliance & Legal",
    tools: [
      { id: "fairhousing", labelKey: "fairhousing", icon: ShieldAlert, free: false },
      { id: "contract", labelKey: "contract", icon: ShieldCheck, free: false },
      { id: "coact", labelKey: "coact", icon: MapPin, free: true },
    ],
  },
];

const DEMO_RESULT = {
  mls: `Welcome home to easy, single-level living where every morning starts with sunlight pouring across gleaming hardwood floors.

Imagine slow Sunday breakfasts in a stunning updated kitchen — granite counters, stainless appliances, and room for everyone to gather while the coffee brews.

Three bedrooms and two baths give your family space to grow, breathe, and unwind. Out back, a fully fenced yard is ready for backyard barbecues, kids' adventures, and pets running free under open skies.

Stroll your little ones to top-rated schools just blocks away, then return to a home that asks nothing of you — no projects, no contractors, no compromises.

Move-in ready. Truly. This one won't last.`,
  instagram: `Bare feet on warm hardwood. Coffee in a kitchen that actually makes you want to cook. A backyard that's entirely, completely yours. 🏡

This 3/2 ranch is everything the algorithm can't capture — updated kitchen, fenced yard, top-rated schools steps away. Move-in ready. Truly.

#JustListed #RealEstate #DreamHome #RanchLife #MoveInReady`,
  facebook: `If you've been waiting for the right one — this is it.

A 3-bed, 2-bath ranch that's genuinely move-in ready. Updated kitchen. Hardwood throughout. Fenced backyard with room to breathe. Walking distance to top-rated schools.

No renovations. No compromises. Just keys.

Comment TOUR and I'll get you in this week.`,
  headlines: ["Three Families Already Toured This. The Fourth One Is Going To Buy It.", "Everything You've Been Compromising On — This House Has It.", "You Said You Wanted Move-In Ready. Here It Is. Now What?"],
  email: `Subject: The ranch on [Street] — you asked me to tell you when something like this came up

Hey [Name],

This is that call.

3 bed, 2 bath ranch. Updated kitchen, hardwood throughout, fenced backyard, top-rated schools walking distance. Move-in ready — genuinely, not just listing-speak.

I've got showings filling up fast. Let me know if you want in before the weekend crowd.`,
  reel_script: `Three bedrooms. Two baths. Fenced backyard.\n\nHardwood throughout — no carpet anywhere.\nUpdated kitchen. Natural light from every angle.\nTop-rated schools two blocks out.\n\nLink in bio. Come see it this weekend.`,
  stories: [
    "Move-in ready means exactly that. No projects. No compromises.",
    "🏠 3 bed · 2 bath · fenced backyard\n✨ Updated kitchen — granite, stainless\n🌳 Top-rated schools 2 blocks away",
    "💬 DM me TOUR and I'll get you in this week",
  ],
  print_flyer: {
    headline: "Move-In Ready. No Compromises.",
    bullets: [
      "Updated Kitchen — granite counters, stainless appliances, zero renovation needed.",
      "Fenced Backyard — private outdoor space ready from day one.",
      "Top Schools Nearby — two blocks to district's highest-rated elementary.",
    ],
    tagline: "Come see why this one won't last.",
  },
  listing_strength: 8.7,
  strength_reasons: ["Strong lifestyle-first narrative", "Urgency without desperation", "Specific features woven into story"],
  tone: "Modern",
  id: "demo-sample-preview",
};

const SAMPLE = `Welcome home to single-level living where every morning starts with sunlight pouring across gleaming hardwood floors. Imagine slow Sunday breakfasts in a stunning updated kitchen — granite counters, stainless appliances, and room for everyone. Three bedrooms. Two baths. A fenced backyard ready for barbecues, kids' adventures, and pets running free. Top-rated schools just blocks away. Move-in ready means exactly that — no projects, no compromises, just keys.`;

const FREE_TRIALS_PER_SESSION = 3;

export default function Playground({ landing = false }) {
  const [raw, setRaw] = useState("");
  const [tone, setTone] = useState("Modern");
  const [meta, setMeta] = useState({ address: "", price: "", beds: "", baths: "", sqft: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("mls");
  const [copiedKey, setCopiedKey] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [showAdvisor, setShowAdvisor] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [strengthOpen, setStrengthOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [emailCaptureOpen, setEmailCaptureOpen] = useState(false);
  const [emailCaptured, setEmailCaptured] = useState(() => !!localStorage.getItem("lw_email_captured"));
  const [isPro, setIsPro] = useState(false);
  const [entitlementsLoaded, setEntitlementsLoaded] = useState(false);
  const [trialRemaining, setTrialRemaining] = useState(null);
  const [mode, setMode] = useState("rewrite");
  const [showAllTools, setShowAllTools] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [openHouseResult, setOpenHouseResult] = useState(null);
  const [fhText, setFhText] = useState("");
  const [fhResult, setFhResult] = useState(null);
  const [gemsLoading, setGemsLoading] = useState(false);
  const [gemsCard, setGemsCard] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [language, setLanguage] = useState(() => localStorage.getItem("lw_language") || "English");
  const [mlsPresetIdx, setMlsPresetIdx] = useState(0);
  const [fhLoading, setFhLoading] = useState(false);
  const [virtualTourUrl, setVirtualTourUrl] = useState("");
  const outputRef = useRef(null);
  const { t } = useTranslation();

  const handleRedfinImport = (data) => {
    setMeta({ address: data.address || "", price: data.price || "", beds: data.beds || "", baths: data.baths || "", sqft: data.sqft || "" });
    if (data.description) setRaw(data.description);
  };

  useEffect(() => {
    if (result && outputRef.current && !landing) {
      outputRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result, landing]);

  useEffect(() => {
    if (landing) handleSample();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landing]);

  useEffect(() => {
    const sid = localStorage.getItem("lw_session_id");
    if (!sid) { setEntitlementsLoaded(true); return; }
    axios.get(`${API}/entitlements/${sid}`)
      .then(({ data }) => setIsPro(data.is_pro || false))
      .catch(() => {})
      .finally(() => setEntitlementsLoaded(true));
  }, []);

  const requirePro = () => setPaywallOpen(true);
  const handleProMode = (m) => setMode(m);

  const handleSample = () => {
  setRaw(SAMPLE);
  setResult(DEMO_RESULT);
  setActiveTab("mls");
};

  const generate = async (forcedTone = null, rawOverride = null) => {
    if (!isPro) { setPaywallOpen(true); return; }
    const listingText = rawOverride || raw;
    if (listingText.trim().length < 10) {
      toast.error("Add at least a sentence — give the AI something to work with.");
      return;
    }
    setLoading(true);
    if (!forcedTone) setResult(null);
    try {
      const session_id = localStorage.getItem("lw_session_id");
      const { data } = await axios.post(`${API}/rewrite`, {
        raw_listing: listingText,
        tone: forcedTone || tone,
        language: language !== "English" ? language : undefined,
        mls_char_limit: MLS_PRESETS[mlsPresetIdx].chars || undefined,
        ...meta,
        virtual_tour_url: virtualTourUrl || undefined,
        session_id,
      });
      setResult(data);
      setActiveTab("mls");
      setShowShareCard(true);
      setTrialRemaining(data.trial_remaining ?? null);
      if (data.trial_remaining === 1 && !emailCaptured) {
        setEmailCaptureOpen(true);
      }
      if (data.trial_remaining === 0) {
        setPaywallOpen(true);
      }
      toast.success(forcedTone ? `Rewritten in ${forcedTone} tone.` : "Listing rewritten — copy & crush it.");
    } catch (e) {
      console.error(e);
      // 402 = trial exceeded or paywall: show modal
      if (e?.response?.status === 402) {
        setTrialRemaining(0);
        setPaywallOpen(true);
        return;
      }
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Generation failed. Try again.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const tryDifferentTone = () => {
    const tones = t("playground.tones");
    const idx = tones.indexOf(tone);
    const next = tones[(idx + 1) % tones.length];
    setTone(next);
    generate(next);
  };

  const copyText = async (key, text) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopiedKey(key);
      toast.success("Copied. Now go crush it.");
      setTimeout(() => setCopiedKey(null), 1600);
    } catch (e) {
      toast.error("Couldn't access clipboard. Select & copy manually.");
    }
  };

  const headlinesAsText = (h) => (h || []).map((x, i) => `${i + 1}. ${x}`).join("\n");
  const storiesToText = (s) => (s || []).map((slide, i) => `Slide ${i + 1}:\n${slide}`).join("\n\n");
  const flyerToText = (f) => f ? `${f.headline}\n\n${(f.bullets || []).join("\n")}\n\n${f.tagline}` : "";
  const currentText = result
    ? activeTab === "headlines" ? headlinesAsText(result.headlines)
    : activeTab === "stories" ? storiesToText(result.stories)
    : activeTab === "print_flyer" ? flyerToText(result.print_flyer)
    : result[activeTab]
    : "";

  const strengthColor = (s) => s >= 8.5 ? "text-emerald-700" : s >= 7 ? "text-vermillion" : "text-amber-600";

  return (
    <section id="playground" data-testid="playground-section" className="border-b border-ink/15">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 md:col-span-3">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-vermillion">{t("playground.sectionLabel")}</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] text-ink">
              <span className="font-light">{t("playground.heading")}</span><br />
              <span className="italic">{t("playground.heading2")}</span>
            </h2>

            <div className="flex flex-wrap gap-3 mt-6">
              {HERO_TOOLS.map((tool) => {
                const Icon = tool.icon;
                return (
                  <button key={tool.id} onClick={() => setMode(tool.id)} data-active={mode === tool.id} className="mode-btn px-4 py-2 font-heading text-xs uppercase tracking-[0.12em] flex items-center gap-2">
                    <Icon className="w-4 h-4" />{t("playground.tools." + tool.labelKey)}
                  </button>
                );
              })}
            </div>

            <div className="relative mt-4">
              <button
                onClick={() => setShowAllTools((o) => !o)}
                className="w-full md:w-auto px-4 py-2.5 font-heading text-xs uppercase tracking-[0.12em] border border-ink/20 hover:border-ink/50 inline-flex items-center gap-2.5 transition"
              >
                <span>{t("playground.toolsLabel") || "Browse All Tools"}</span>
                <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-ink/40 bg-ink/5 px-1.5 py-0.5">
                  {TOOL_CATEGORIES.flatMap((c) => c.tools).length} tools
                </span>
                {showAllTools ? <span>▲</span> : <span>▾</span>}
              </button>

              {showAllTools && (
                <div className="mt-2 z-20 w-full md:w-[620px] max-h-[70vh] overflow-auto bg-white border border-ink/20 shadow-2xl shadow-ink/10 p-5 animate-rise">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                    {TOOL_CATEGORIES.map((cat) => (
                      <div key={cat.name}>
                        <h4 className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">{cat.name}</h4>
                        <div className="space-y-0.5">
                          {cat.tools.map((tool) => {
                            const Icon = tool.icon;
                            return (
                              <button
                                key={tool.id}
                                onClick={() => { setMode(tool.id); setShowAllTools(false); }}
                                data-active={mode === tool.id}
                                className="w-full flex items-center gap-2 px-2.5 py-1.5 font-heading text-[10px] uppercase tracking-[0.1em] text-left hover:bg-ink/5 transition data-[active=true]:bg-ink data-[active=true]:text-oat rounded"
                              >
                                <Icon className="w-3 h-3 shrink-0" />
                                <span className="flex-1">{t("playground.tools." + tool.labelKey)}</span>
                                {!tool.free && <Lock className="w-2.5 h-2.5 text-ink/30" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {mode === "rewrite" && (
        <div className="grid grid-cols-12 gap-px bg-ink/15 border border-ink/15">
          {/* Input column */}
          <div className="col-span-12 lg:col-span-5 bg-oat p-6 md:p-8">
            <div className="flex items-center justify-between mb-4">
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink/60">{t("playground.inputLabel")}</span>
              <button data-testid="sample-btn" onClick={handleSample} className="font-mono text-[11px] tracking-[0.15em] uppercase text-vermillion hover:underline">
                {t("playground.useSample")}
              </button>
            </div>
            <textarea
              data-testid="raw-listing-textarea"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={t("playground.placeholder")}
              rows={8}
              className="editorial-input mb-5"
            />

            <div className="mb-5">
              <MissingDetailsPanel rawListing={raw} onFillDetails={(fullText) => setRaw(fullText)} />
            </div>

            {raw?.trim().length > 20 && (
              <div className="mb-5">
                <AskListingChat rawListing={raw} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div className="col-span-2">
                <input data-testid="meta-address" placeholder={t("playground.meta.address")} value={meta.address} onChange={(e) => { setMeta({ ...meta, address: e.target.value }); setGemsCard(null); }} className="editorial-input text-sm w-full" />
                {meta.address?.trim().length > 5 && !gemsCard && (
                  <button
                    type="button"
                    disabled={gemsLoading}
                    onClick={async () => {
                      setGemsLoading(true);
                      try {
                        const { data } = await axios.post(`${API}/local-gems`, {
                          address: meta.address,
                          session_id: localStorage.getItem("lw_session_id"),
                        });
                        setGemsCard(data.paragraph);
                      } catch {
                        toast.error("Couldn't fetch neighborhood data. Try again.");
                      } finally {
                        setGemsLoading(false);
                      }
                    }}
                    className="mt-2 w-full flex items-center justify-center gap-2 border border-vermillion text-vermillion hover:bg-vermillion hover:text-oat px-4 py-2 font-heading text-[11px] uppercase tracking-[0.12em] transition disabled:opacity-50"
                  >
                    {gemsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {gemsLoading ? t("playground.gems.loading") : t("playground.gems.button")}
                  </button>
                )}
                {gemsCard && (
                  <div className="mt-2 border border-ink/15 bg-white p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-vermillion">{t("playground.gems.heading")}</span>
                      <button onClick={() => setGemsCard(null)} className="font-mono text-[10px] text-ink/40 hover:text-ink">✕</button>
                    </div>
                    <p className="font-body text-sm text-ink/80 leading-relaxed">{gemsCard}</p>
                    <button
                      onClick={() => {
                        setRaw(prev => prev ? `${prev}\n\n${gemsCard}` : gemsCard);
                        toast.success(t("playground.gems.added"));
                        setGemsCard(null);
                      }}
                      className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-vermillion hover:underline"
                    >
                      <Sparkles className="w-3 h-3" /> {t("playground.gems.add")}
                    </button>
                  </div>
                )}
              </div>
              {meta.address?.trim().length > 10 && !meta.beds && (
                <div className="col-span-2 -mt-1">
                  <button
                    type="button"
                    disabled={lookupLoading}
                    onClick={async () => {
                      setLookupLoading(true);
                      try {
                        const { data } = await axios.post(`${API}/lookup-address`, {
                          address: meta.address,
                          session_id: localStorage.getItem("lw_session_id"),
                        });
                        setMeta(prev => ({
                          ...prev,
                          price: data.price || prev.price,
                          beds: data.beds || prev.beds,
                          baths: data.baths || prev.baths,
                          sqft: data.sqft || prev.sqft,
                        }));
                        toast.success(t("playground.lookup.success"));
                      } catch (e) {
                        const msg = e?.response?.data?.detail || t("playground.lookup.notFound");
                        toast.info(msg);
                      } finally {
                        setLookupLoading(false);
                      }
                    }}
                    className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-vermillion hover:underline disabled:opacity-50"
                  >
                    {lookupLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {lookupLoading ? t("playground.lookup.loading") : t("playground.lookup.button")}
                  </button>
                </div>
              )}
              <input data-testid="meta-price" placeholder={t("playground.meta.price")} value={meta.price} onChange={(e) => setMeta({ ...meta, price: e.target.value })} className="editorial-input text-sm" />
              <input data-testid="meta-beds" placeholder={t("playground.meta.beds")} value={meta.beds} onChange={(e) => setMeta({ ...meta, beds: e.target.value })} className="editorial-input text-sm" />
              <input data-testid="meta-baths" placeholder={t("playground.meta.baths")} value={meta.baths} onChange={(e) => setMeta({ ...meta, baths: e.target.value })} className="editorial-input text-sm" />
              <input data-testid="meta-sqft" placeholder={t("playground.meta.sqft")} value={meta.sqft} onChange={(e) => setMeta({ ...meta, sqft: e.target.value })} className="editorial-input text-sm col-span-2" />
              <div className="col-span-2">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="w-3.5 h-3.5 text-vermillion" strokeWidth={2} />
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-ink/60">{t("playground.meta.virtualTour")}</span>
                </div>
                <input
                  data-testid="virtual-tour-url"
                  type="url"
                  placeholder={t("playground.meta.virtualTourPlaceholder")}
                  value={virtualTourUrl}
                  onChange={(e) => setVirtualTourUrl(e.target.value)}
                  className="editorial-input text-sm w-full"
                />
                <span className="font-mono text-[9px] tracking-[0.1em] uppercase text-ink/40 block mt-1">Matterport · Kuula · CloudPano · Zillow 3D</span>
              </div>
            </div>

            <div className="mb-5">
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink/60 block mb-3">{t("playground.tone")}</span>
              <div className="flex flex-wrap gap-2">
                {t("playground.tones").map((toneLabel) => (
                  <button key={toneLabel} data-testid={`tone-${toneLabel.toLowerCase()}-btn`} onClick={() => setTone(toneLabel)} data-active={tone === toneLabel} className="tone-pill">
                    {toneLabel}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink/60 block mb-3">{t("playground.outputLang")}</span>
              <div className="flex flex-wrap gap-2">
                {[{ code: "English", flag: "🇺🇸" }, { code: "Spanish", flag: "🇪🇸" }].map(({ code, flag }) => (
                  <button key={code} onClick={() => { setLanguage(code); if (result && code !== language) toast.info(`Language set to ${code} — hit Rewrite to apply.`); }} data-active={language === code} className="tone-pill flex items-center gap-1.5">
                    <span>{flag}</span>{code}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink/60 block mb-3">{t("playground.mlsLimit")}</span>
              <select
                value={mlsPresetIdx}
                onChange={(e) => setMlsPresetIdx(Number(e.target.value))}
                className="w-full border border-ink/20 px-3 py-2.5 font-body text-sm outline-none focus:border-vermillion transition bg-white"
              >
                {t("playground.mlsPresets").map((label, i) => (
                  <option key={i} value={i}>{label}</option>
                ))}
              </select>
            </div>

            <button
              data-testid="generate-btn"
              onClick={() => landing ? setPaywallOpen(true) : generate()}
              disabled={loading}
              className="btn-vermillion w-full px-7 py-4 font-heading text-sm uppercase tracking-[0.15em] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />{t("playground.rewriting")}</>) : landing ? (<><Sparkles className="w-4 h-4" />{t("playground.startFree")}</>) : (<><Sparkles className="w-4 h-4" />{t("playground.rewrite")}</>)}
            </button>
            {result && !landing && (
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="w-full mt-2 px-7 py-3 font-heading text-xs uppercase tracking-[0.15em] border border-ink/20 hover:border-ink/50 flex items-center justify-center gap-2 transition"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                View Full Marketing Package ({['MLS', 'IG', 'FB', 'Email', 'Reel', 'Stories', 'Flyer'].length} outputs)
              </button>
            )}
            <p className="mt-3 font-mono text-[10px] tracking-[0.15em] uppercase text-ink/50 text-center">
              3 free rewrites, then from $19/mo · cancel anytime
            </p>
          </div>

          {/* Output column */}
          <div className="col-span-12 lg:col-span-7 bg-white p-6 md:p-8 min-h-[560px] flex flex-col" ref={outputRef}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex flex-wrap gap-2">
                {TABS.map((t) => (
                  <button key={t.key} data-testid={`tab-${t.key}-btn`} onClick={() => setActiveTab(t.key)} data-active={activeTab === t.key} className="tab-pill px-3.5 py-2 font-heading text-[12px] uppercase tracking-[0.1em]">
                    <span className="mr-1.5">{t.icon}</span>{t.label.split(" ")[0]}
                  </button>
                ))}
              </div>
              {result && (
                <div className="flex items-center gap-3">
                  <button
                    data-testid="strength-badge"
                    onClick={() => setStrengthOpen((o) => !o)}
                    className="flex items-center gap-2 px-3 py-1.5 border border-ink/20 hover:border-ink transition font-mono text-[11px] tracking-[0.12em] uppercase"
                  >
                    <Star className={`w-3.5 h-3.5 ${strengthColor(result.listing_strength)} fill-current`} />
                    Strength {result.listing_strength?.toFixed(1)}/10
                  </button>
                  <button
                    data-testid="copy-output-btn"
                    onClick={() => copyText(activeTab, currentText)}
                    className="bg-ink text-oat hover:bg-vermillion px-3.5 py-2 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition"
                  >
                    {copiedKey === activeTab ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedKey === activeTab ? "Copied" : "Copy & Crush It"}
                  </button>
                  <button
                    data-testid="share-rewrite-btn"
                    onClick={() => {
                      const url = `${window.location.origin}/share/${result.id}`;
                      navigator.clipboard.writeText(url);
                      toast.success("Share link copied — paste anywhere!");
                    }}
                    className="bg-vermillion text-oat hover:bg-[#ff2a0e] px-3.5 py-2 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition"
                    title="Copy a public share link to show this rewrite"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    Share
                  </button>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/share/${result.id}`;
                      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(url)}`;
                      window.open(qrUrl, '_blank', 'width=600,height=600');
                    }}
                    className="bg-zinc-800 text-zinc-100 hover:bg-zinc-700 px-3.5 py-2 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition"
                    title="Generate QR code for this listing — put it on your sign!"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="4" height="4"/><rect x="3" y="17" width="4" height="4"/><rect x="17" y="3" width="4" height="4"/><path d="M17 17h4v4h-4z"/><path d="M11 7h2v10h-2z"/><path d="M7 11h10v2H7z"/></svg>
                    QR
                  </button>
                </div>
              )}
            </div>

            {result && strengthOpen && (
              <div data-testid="strength-explainer" className="mb-4 border border-ink/15 bg-oat p-4 animate-rise">
                <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-ink/60 mb-2">
                  <Star className="w-3.5 h-3.5" /> Why {result.listing_strength?.toFixed(1)} / 10
                </div>
                <ul className="space-y-1.5 font-body text-sm text-ink/80">
                  {(result.strength_reasons || []).map((r, i) => (
                    <li key={i} className="flex gap-2"><span className="text-vermillion">·</span> {r}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex-1 border-t border-ink/15 pt-6">
              {!result && !loading && (
                <div className="h-full flex flex-col items-start justify-center max-w-md">
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink/40 mb-3">Awaiting input</span>
                  <p className="font-display italic text-3xl md:text-4xl leading-tight text-ink/70">
                    Your rewritten listing will materialize here.
                  </p>
                  <p className="mt-4 font-body text-ink/60">
                    Paste a draft, pick a tone, hit rewrite. Five publish-ready assets in about ten seconds.
                  </p>
                </div>
              )}
              {loading && (
                <div className="h-full flex flex-col items-start justify-center">
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-vermillion mb-3 streaming-cursor">Generating</span>
                  <div className="space-y-3 w-full max-w-2xl">
                    {[11,10,8,11,9].map((w,i)=>(<div key={i} className={`h-3 bg-ink/10 w-${w}/12 animate-pulse`} />))}
                  </div>
                </div>
              )}
              {result && (
                activeTab === "headlines" ? (
                  <ol data-testid="output-headlines" className="space-y-4 font-display text-2xl md:text-3xl leading-tight">
                    {(result.headlines || []).map((h, i) => (
                      <li key={i} className="flex gap-4 group">
                        <span className="font-mono text-[11px] tracking-widest text-vermillion mt-2 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                        <div className="flex-1">
                          <span className="italic">{h}</span>
                          <button
                            data-testid={`copy-headline-${i}`}
                            onClick={() => copyText(`headline-${i}`, h)}
                            className="ml-3 inline-flex items-center gap-1 font-body text-[10px] tracking-[0.15em] uppercase text-ink/40 hover:text-vermillion transition"
                          >
                            {copiedKey === `headline-${i}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copiedKey === `headline-${i}` ? "Copied" : "Copy"}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : activeTab === "stories" ? (
                  <div data-testid="output-stories" className="space-y-3">
                    {(result.stories || []).map((slide, i) => (
                      <div key={i} className="border border-ink/15 p-4 relative">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink/30 absolute top-2 left-3">Slide {i + 1}</span>
                        <pre className="whitespace-pre-wrap font-mono text-[13px] leading-[1.7] text-ink mt-4">{slide}</pre>
                        <button onClick={() => copyText(`story-${i}`, slide)}
                          className="mt-2 inline-flex items-center gap-1 font-body text-[10px] tracking-[0.15em] uppercase text-ink/40 hover:text-vermillion transition">
                          {copiedKey === `story-${i}` ? <><Check className="w-3 h-3" />Copied</> : <><Copy className="w-3 h-3" />Copy slide</>}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : activeTab === "print_flyer" && result.print_flyer ? (
                  <div data-testid="output-print-flyer" className="space-y-5">
                    <div className="border-l-4 border-vermillion pl-4">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink/30 block mb-1">Headline</span>
                      <p className="font-display text-2xl md:text-3xl leading-tight">{result.print_flyer.headline}</p>
                    </div>
                    <div className="space-y-2">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink/30 block">Bullet Points</span>
                      {(result.print_flyer.bullets || []).map((b, i) => (
                        <div key={i} className="flex gap-3 items-start">
                          <span className="text-vermillion mt-1 text-xs">▪</span>
                          <p className="font-body text-sm leading-relaxed text-ink">{b}</p>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-ink/10 pt-4">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink/30 block mb-1">Tagline</span>
                      <p className="font-body text-base italic text-ink/70">{result.print_flyer.tagline}</p>
                    </div>
                  </div>
                ) : activeTab === "reel_script" && result.reel_script ? (
                  <div data-testid="output-reel" className="space-y-4">
                    <div className="bg-ink/5 border border-ink/10 rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        <span className="ml-2 font-mono text-[9px] tracking-[0.2em] uppercase text-ink/30">REC · 0:15 / 0:30</span>
                      </div>
                      <div className="font-body text-sm leading-relaxed whitespace-pre-wrap text-ink">{result.reel_script}</div>
                    </div>
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase text-ink/40 bg-oat p-3 border border-ink/10 rounded">
                      <Film className="w-3.5 h-3.5 text-vermillion" />
                      Pro tip: Read this straight into your phone camera. No teleprompter needed.
                    </div>
                  </div>
                ) : (
                  <pre data-testid={`output-${activeTab}`} className="whitespace-pre-wrap font-mono text-[13px] leading-[1.7] text-ink">
                    {result[activeTab]}
</pre>
                )
              )}

              {/* 360° Virtual Tour embed */}
              {result?.virtual_tour_url && (
                <div className="mt-6 border-t-4 border-vermillion pt-5 bg-oat/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="bg-vermillion text-white w-8 h-8 rounded-full flex items-center justify-center">
                        <Box className="w-4 h-4" />
                      </div>
                      <span className="font-heading text-sm uppercase tracking-[0.15em] text-ink">360° Virtual Tour</span>
                    </div>
                    <a
                      href={result.virtual_tour_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-vermillion hover:text-[#ff2a0e] font-mono text-[10px] tracking-[0.15em] uppercase transition"
                    >
                      Open Full Screen →
                    </a>
                  </div>
                  <div className="relative w-full border-2 border-vermillion/30 rounded-lg overflow-hidden shadow-lg" style={{ paddingBottom: "56.25%" }}>
                    <iframe
                      src={result.virtual_tour_url}
                      className="absolute top-0 left-0 w-full h-full border-0 bg-ink/5"
                      allow="fullscreen; vr"
                      title="360 Virtual Tour"
                      loading="lazy"
                    />
                  </div>
                </div>
              )}
            </div>

            {result && !loading && (
              <div className="mt-6 pt-5 border-t border-ink/10">
                <div className="flex items-center gap-2 mb-4">
                  <Share2 className="w-4 h-4 text-vermillion" />
                  <span className="font-heading text-sm uppercase tracking-[0.12em] text-ink">Share Your Transformation</span>
                  <span className="ml-2 font-mono text-[9px] tracking-[0.15em] uppercase bg-vermillion text-oat px-1.5 py-0.5">Free marketing</span>
                  <button onClick={() => setShowShareCard(!showShareCard)} className="ml-auto font-mono text-[10px] text-ink/40 hover:text-ink">
                    {showShareCard ? "▲ hide" : "▼ show"}
                  </button>
                </div>
                {showShareCard && (
                  <div className="mb-6 space-y-4">
                    <ShareCard rawListing={raw || ""} rewrittenListing={typeof currentText === 'string' ? currentText : JSON.stringify(currentText || "")} tone={tone} />
                    <ViralPostCard rawListing={raw || ""} rewrittenListing={typeof currentText === 'string' ? currentText : JSON.stringify(currentText || "")} tone={tone} />
                  </div>
                )}
                <div className="flex flex-wrap gap-2.5 mb-5">
                  <button
                    data-testid="open-video-btn"
                    onClick={() => setShowVideo(true)}
                    className="bg-vermillion text-oat hover:bg-[#ff2a0e] px-4 py-2.5 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition hover:-translate-y-0.5"
                  >
                    <Film className="w-3.5 h-3.5" /> Turn This Into a Video
                  </button>
                  <button
                    data-testid="advisor-btn"
                    onClick={() => setShowAdvisor(true)}
                    className="border border-ink/30 hover:bg-ink hover:text-oat px-4 py-2.5 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition hover:-translate-y-0.5"
                  >
                    <Bot className="w-3.5 h-3.5" /> AI Advisor
                  </button>
                  <button
                    data-testid="try-different-tone-btn"
                    onClick={tryDifferentTone}
                    disabled={loading}
                    className="border border-ink/30 hover:bg-ink hover:text-oat px-4 py-2.5 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    <RefreshCcw className="w-3.5 h-3.5" /> Try a Different Tone
                  </button>
                  <button
                    data-testid="auto-post-btn"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/social/post-direct`, {
                          text: result?.facebook || result?.mls || "",
                          platform: "facebook",
                        });
                        toast.success("Saved to social queue ✓");
                      } catch {
                        toast.error("Couldn't save post.");
                      }
                    }}
                    className="border border-ink/30 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] px-4 py-2.5 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition hover:-translate-y-0.5"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Save to Social
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const { data } = await axios.post(`${API}/export/pdf`, {
                          content: result?.mls || "",
                          title: "Listing Description",
                        });
                        const win = window.open();
                        if (win) {
                          win.document.write(data.html);
                          win.document.close();
                          win.print();
                        }
                        toast.success("PDF opened in print preview ✓");
                      } catch {
                        toast.error("PDF export failed.");
                      }
                    }}
                    className="border border-ink/30 hover:bg-ink hover:text-oat px-4 py-2.5 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition hover:-translate-y-0.5"
                  >
                    <Box className="w-3.5 h-3.5" /> Export PDF
                  </button>
                  <button
                    data-testid="make-10-btn"
                    onClick={async () => {
                      if (!result?.id) return;
                      setLoading(true);
                      try {
                        const session_id = localStorage.getItem("lw_session_id");
                        const { data } = await axios.post(
                          `${API}/rewrite/enhance`,
                          { listing_id: result.id, session_id }
                        );
                        setResult(data);
                        setActiveTab("mls");
                        toast.success("Elevated to 10/10. That's the one.");
                      } catch (e) {
                        if (e?.response?.status === 402) {
                          setPaywallOpen(true);
                          return;
                        }
                        toast.error(e?.response?.data?.detail || "Enhance failed. Try again.");
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    className="border border-vermillion text-vermillion hover:bg-vermillion hover:text-oat px-4 py-2.5 font-heading text-[11px] uppercase tracking-[0.12em] flex items-center gap-2 transition hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    <Flame className="w-3.5 h-3.5" /> Make It 10/10 — Pro
                  </button>
                </div>

                <div className="flex items-center justify-between font-mono text-[11px] tracking-[0.12em] uppercase text-ink/50">
                  <span>Tone · {result.tone}</span>
                  <span>ID · {result.id?.slice(0, 8)}</span>
</div>

            <button
              onClick={() => setShowAllTools((o) => !o)}
              className="mt-3 font-mono text-[10px] tracking-[0.15em] uppercase text-ink/40 hover:text-ink transition flex items-center gap-1.5"
            >
              {showAllTools ? "Close ▲" : `${TOOL_CATEGORIES.flatMap((c) => c.tools).length} tools · See All ▾`}
            </button>

            {showAllTools && (
              <div className="mt-3 bg-oat border border-ink/15 p-5 rounded animate-rise">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                  {TOOL_CATEGORIES.map((cat) => (
                    <div key={cat.name}>
                      <h4 className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mb-2">{cat.name}</h4>
                      <div className="space-y-0.5">
                        {cat.tools.map((tool) => {
                          const Icon = tool.icon;
                          return (
                            <button
                              key={tool.id}
                              onClick={() => { setMode(tool.id); setShowAllTools(false); }}
                              data-active={mode === tool.id}
                              className="w-full flex items-center gap-2 px-2.5 py-1.5 font-heading text-[10px] uppercase tracking-[0.1em] text-left hover:bg-ink/5 transition data-[active=true]:bg-ink data-[active=true]:text-oat rounded"
                            >
                              <Icon className="w-3 h-3 shrink-0" />
                              <span className="flex-1">{t("playground.tools." + tool.labelKey)}</span>
                              {!tool.free && <Lock className="w-2.5 h-2.5 text-ink/30" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
            )}
          </div>
        </div>
        )}

        </div>

      {mode === "expired" && (isPro ? (
        <div className="bg-white border border-ink/15 p-8 mt-px">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">NEW</span>
            <h3 className="font-heading text-xl tracking-wide text-ink">Expired Listing Scripts</h3>
          </div>
          <p className="text-ink/60 text-sm mb-4">Turn expired listings into new opportunities. Get cold call scripts, voicemails, texts, and door knock scripts for any property that didn't sell.</p>
          <ExpiredListingScripts />
        </div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="expired" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "import" && (isPro ? (
        <div className="bg-oat border border-ink/15 p-8 mt-px">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">NEW</span>
              <h3 className="font-heading text-xl tracking-wide text-ink">One-Click Redfin Import</h3>
            </div>
            <p className="text-ink/60 text-sm mb-4">Paste any Redfin listing URL to auto-import address, price, beds, baths, sqft, and description. Then switch to "Listing Rewrite" to generate copy.</p>
            <RedfinImport onImport={handleRedfinImport} />
          </div>
          {meta.address && (
            <div className="mt-6 p-4 bg-white border border-ink/10">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-ink/60 mb-2">Imported</p>
              <p className="text-ink font-medium">{meta.address}</p>
              {meta.price && <p className="text-ink/70">{meta.price} · {meta.beds}bd · {meta.baths}ba · {meta.sqft}sf</p>}
              <p className="text-ink/50 text-sm mt-2">Switch to "Rewrite" mode to generate copy.</p>
            </div>
          )}
        </div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="import" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "contract" && (isPro ? (
        <div className="bg-oat border border-ink/15 p-8 mt-px">
          <ContractReview />
        </div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="contract" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "seller" && (isPro ? (
        <div className="bg-oat border border-ink/15 p-8 mt-px">
          <SellerDashboard />
        </div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="seller" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "nurture" && (isPro ? (
        <div className="bg-oat border border-ink/15 p-8 mt-px">
          <LeadNurture />
        </div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="nurture" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "score" && (isPro ? (
        <div className="bg-oat border border-ink/15 p-8 mt-px">
          <LeadScore />
        </div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="score" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "transaction" && (isPro ? (
        <div className="bg-oat border border-ink/15 p-8 mt-px">
          <TransactionTracker />
        </div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="transaction" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "openhouse" && (isPro ? (
        <OpenHousePanel result={openHouseResult} setResult={setOpenHouseResult} />
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="openhouse" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "fairhousing" && (isPro ? (
        <FairHousingPanel text={fhText} setText={setFhText} result={fhResult} setResult={setFhResult} loading={fhLoading} setLoading={setFhLoading} />
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="fairhousing" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "coact" && (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <COActPanel listingText={output} agentName={brandVoice?.name || ""} />
        </div>
      )}

      {mode === "voice" && (isPro ? (
        <VoiceDescriptionPanel setRaw={(text) => { setRaw(text); setMode("rewrite"); }} setMode={setMode} />
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="voice" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "report" && (isPro ? (
        <PostSaleReportPanel />
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="report" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "bio" && (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <AgentBioPanel />
        </div>
      )}

      {mode === "photo" && (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <PhotoToListing onFeaturesDetected={(draft) => { setRaw(draft); setMode("rewrite"); }} />
        </div>
      )}

      {mode === "batch" && (isPro ? (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <BatchGenerator />
        </div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="batch" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "referral" && (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <ReferralPanel />
        </div>
      )}

      {mode === "brandvoice" && (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <BrandVoicePanel />
        </div>
      )}

      {mode === "templates" && (isPro ? (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><TemplatesPanel /></div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="templates" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "calendar" && (isPro ? (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ContentCalendarPanel /></div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="calendar" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "marketupdate" && (isPro ? (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><MarketUpdatePanel /></div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="marketupdate" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "scheduler" && (isPro ? (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><SchedulerPanel /></div>
      ) : (<div className="bg-white border border-ink/15 p-8 md:p-10 mt-px"><ProToolPreview tool="scheduler" onUnlock={() => setPaywallOpen(true)} /></div>))}

      {mode === "agentpage" && (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <AgentPageSetup />
        </div>
      )}

      {mode === "cma" && (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <CMAPanel />
        </div>
      )}

      {mode === "staging" && (
        <div className="bg-white border border-ink/15 p-8 md:p-10 mt-px">
          <VirtualStagingPanel />
        </div>
      )}

      {showVideo && result && (
        <VideoBuilder
          listing={result}
          onClose={() => setShowVideo(false)}
        />
      )}
      {showVideo && result && (
        <div data-testid="pro-upsell-banner" className="mt-px bg-coal text-oat p-7 md:p-9 border border-ink/15 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-vermillion">ListWorks PRO</span>
            <p className="mt-2 font-display italic text-2xl md:text-3xl leading-tight">
              Unlimited rewrites · cinematic videos without watermarks · the AI Advisor in your pocket.
            </p>
          </div>
          <div className="flex md:justify-end">
            <button
              data-testid="pro-upgrade-btn"
              onClick={async () => { await import("@/lib/checkout").then(m => m.startCheckout("pro_month")); }}
              className="bg-vermillion text-oat hover:bg-[#ff2a0e] px-6 py-4 font-heading text-sm uppercase tracking-[0.15em] transition hover:-translate-y-1 inline-flex items-center gap-2"
            >
              Get Pro — $29/mo
            </button>
          </div>
        </div>
      )}
      {showAdvisor && (
        <AdvisorPanel
          listingId={result?.id}
          onClose={() => setShowAdvisor(false)}
        />
      )}
      {emailCaptureOpen && (
        <EmailCapture
          onClose={() => setEmailCaptureOpen(false)}
          onBonusGranted={() => {
            localStorage.setItem("lw_email_captured", "1");
            setEmailCaptured(true);
            setEmailCaptureOpen(false);
            setTrialRemaining(3);
          }}
        />
      )}
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        freeUsed={FREE_TRIALS_PER_SESSION}
        trialRemaining={trialRemaining}
      />
    </section>
  );
}
